import { type ReactElement, useEffect, useState } from 'react';
import { AdminLayout } from './layout.tsx';
import { adminApi, type AdminAuditLogEntry } from '../../state/admin/api.ts';
import { Button, Card, Heading } from '@/components/ui';

export function AuditLogPage(): ReactElement {
  const [items, setItems] = useState<AdminAuditLogEntry[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);

  async function load(next?: string): Promise<void> {
    const body = await adminApi.listAuditLog({
      limit: 20,
      cursor: next,
    });

    if (!body.ok) return;

    setItems((prev) => (next ? [...prev, ...body.data.items] : body.data.items));
    setCursor(body.data.next_cursor);
  }

  useEffect(() => {
    void load();
  }, []);

  return (
    <AdminLayout title="Audit Log" eyebrow="Admin · Ops">
      <Card variant="default" className="mt-6 overflow-hidden p-0">
        <header className="border-b border-line px-6 py-4">
          <Heading level="h2" className="text-title font-medium text-ink">
            Audit Events
          </Heading>
        </header>
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-small">
            <thead>
              <tr className="border-b border-line">
                {(['Action', 'Time', 'Admin', 'Details'] as const).map((col) => (
                  <th
                    key={col}
                    scope="col"
                    className="px-6 py-3 text-left font-sans text-eyebrow font-medium uppercase tracking-wide text-muted"
                  >
                    {col}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {items.length === 0 ? (
                <tr>
                  <td
                    colSpan={4}
                    className="px-6 py-10 text-center font-sans text-small text-muted"
                  >
                    No audit events yet.
                  </td>
                </tr>
              ) : (
                items.map((item) => (
                  <tr key={item.id} className="border-b border-line last:border-b-0">
                    <td className="px-6 py-3 font-mono text-mono text-ink">
                      {item.action_type}
                    </td>
                    <td className="px-6 py-3 font-sans text-small text-ink-2">
                      {item.created_at}
                    </td>
                    <td className="px-6 py-3 font-mono text-mono text-ink-2">
                      {item.admin_user_id.slice(0, 8)}…
                    </td>
                    <td className="px-6 py-3">
                      <pre className="font-mono text-mono text-muted whitespace-pre-wrap break-all">
                        {JSON.stringify(item.payload, null, 2)}
                      </pre>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {cursor ? (
        <div className="mt-4 flex justify-center">
          <Button variant="secondary" size="sm" type="button" onClick={() => void load(cursor)}>
            Load more
          </Button>
        </div>
      ) : null}
    </AdminLayout>
  );
}
